/**
 * Rate Limiter per limitare le chiamate API dal frontend
 * Previene abusi e protegge le risorse del backend
 */

import { secureLog } from '@/utils/secureLogger';

interface RateLimitConfig {
  maxRequests: number; // Numero massimo di richieste
  windowMs: number; // Finestra temporale in millisecondi
  keyPrefix: string; // Prefisso per identificare il tipo di richiesta
}

interface RateLimitEntry {
  count: number;
  resetTime: number;
}

class RateLimiter {
  private limits: Map<string, RateLimitEntry> = new Map();
  private cleanupInterval: number | null = null;

  constructor() {
    // Pulizia automatica ogni minuto
    this.cleanupInterval = window.setInterval(() => {
      this.cleanup();
    }, 60000);
  }

  /**
   * Verifica se una richiesta è permessa
   * @param key Chiave identificativa della richiesta
   * @param config Configurazione del rate limit
   * @returns true se la richiesta è permessa, false altrimenti
   */
  public isAllowed(key: string, config: RateLimitConfig): boolean {
    const fullKey = `${config.keyPrefix}:${key}`;
    const now = Date.now();
    
    let entry = this.limits.get(fullKey);
    
    // Se non esiste o è scaduto, crea una nuova entry
    if (!entry || now > entry.resetTime) {
      entry = {
        count: 1,
        resetTime: now + config.windowMs
      };
      this.limits.set(fullKey, entry);
      return true;
    }
    
    // Se il limite è raggiunto, nega la richiesta
    if (entry.count >= config.maxRequests) {
      const remainingSeconds = Math.ceil((entry.resetTime - now) / 1000);
      secureLog.warn('Rate limit exceeded:', { key: fullKey, remainingSeconds });
      return false;
    }
    
    // Incrementa il contatore
    entry.count++;
    this.limits.set(fullKey, entry);
    return true;
  }

  /**
   * Esegue una funzione con rate limiting
   * @param key Chiave identificativa della richiesta
   * @param config Configurazione del rate limit
   * @param fn Funzione da eseguire
   * @returns Risultato della funzione o errore se rate limit superato
   */
  public async execute<T>(
    key: string,
    config: RateLimitConfig,
    fn: () => Promise<T>
  ): Promise<T> {
    if (!this.isAllowed(key, config)) {
      throw new Error(
        'Troppe richieste. Per favore riprova tra qualche secondo.'
      );
    }
    
    return await fn();
  }

  /**
   * Resetta il rate limit per una chiave specifica
   * @param key Chiave da resettare
   * @param keyPrefix Prefisso della chiave
   */
  public reset(key: string, keyPrefix: string): void {
    const fullKey = `${keyPrefix}:${key}`;
    this.limits.delete(fullKey);
  }

  /**
   * Rimuove le entry scadute
   */
  private cleanup(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];
    
    this.limits.forEach((entry, key) => {
      if (now > entry.resetTime) {
        keysToDelete.push(key);
      }
    });
    
    keysToDelete.forEach(key => this.limits.delete(key));
  }

  /**
   * Distrugge il rate limiter
   */
  public destroy(): void {
    if (this.cleanupInterval !== null) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    this.limits.clear();
  }
}

// Istanza singleton del rate limiter
const rateLimiter = new RateLimiter();

// Configurazioni predefinite per diversi tipi di richieste (ottimizzate per Teable Plus)
export const RATE_LIMIT_CONFIGS = {
  // Ricerca promozioni: max 200 richieste per minuto
  SEARCH_PROMOS: {
    maxRequests: 200,
    windowMs: 60000,
    keyPrefix: 'search'
  },
  // Creazione record: max 50 richieste per minuto
  CREATE_RECORD: {
    maxRequests: 50,
    windowMs: 60000,
    keyPrefix: 'create'
  },
  // Lettura record: max 300 richieste per minuto
  READ_RECORDS: {
    maxRequests: 300,
    windowMs: 60000,
    keyPrefix: 'read'
  },
  // Update/Delete: max 100 richieste per minuto
  MODIFY_RECORD: {
    maxRequests: 100,
    windowMs: 60000,
    keyPrefix: 'modify'
  }
};

export { rateLimiter };
export type { RateLimitConfig };